import React, { useState, useCallback } from "react";
import {
  Button,
  Grid,
  TextField,
  Typography,
  Paper,
  Stack,
  Divider,
  Switch,
  FormControlLabel,
  Chip,
  CircularProgress,
} from "@mui/material";
import { Download, Visibility, Save, Update } from "@mui/icons-material";
import { PDFDownloadLink } from "@react-pdf/renderer";
import { toast } from "react-toastify";
import InvoiceDocument from "../../InvoiceDocument";
import LineItemsTable from "./LineItemsTable";
import ProformaPreviewModal from "./ProformaPreviewModal";
import { post } from "../../../../../services";
import { CREATE_PREFORMA_INVOICE, UPDATE_PREFORMA_INVOICE } from "../../../../../graphql";

const ProformaSection = ({
  bookingDetails,
  hotelProfile,
  colors,
  invoiceData,
  lineItems,
  grandTotal,
  isEditing,
  onHeaderChange,
  onLineItemChange,
  onAddItem,
  onRemoveItem,
  onToggleEditing,
  gstEnabled,
  savedPreforma,
  isLoadingPreforma,
  preformaSource,
  setSavedPreforma,
  setPreformaSource,
}) => {
  const [openPreview, setOpenPreview] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const openPreviewModal = useCallback(() => setOpenPreview(true), []);
  const closePreviewModal = useCallback(() => setOpenPreview(false), []);

  // Shared payload builder — Updated to map camelCase -> snake_case
  const buildInvoicePayload = useCallback(() => {
    return {
      bookingId: bookingDetails.booking_id,
      invoiceNumber: invoiceData.invoiceNumber,
      invoiceDate: invoiceData.invoiceDate?.toISOString(),
      billingEntity: {
        name: invoiceData.guestName,
        address: invoiceData.guestAddress,
        email: invoiceData.guestEmail,
        mobile: invoiceData.guestMobile,
        gstin: invoiceData.guestGstin,
      },
      lineItems: lineItems.map((item) => ({
        id: item.id,
        description: item.description,
        qty: Number(item.qty),
        
        // --- FIX: Map Frontend (camelCase) to Backend (snake_case) ---
        inclusive_rate: Number(item.inclusiveRate), 
        inclusive_amount: Number(item.inclusiveAmount),
        tax_rate: Number(item.taxRate),
        // -------------------------------------------------------------

        base: Number(item.base),
        sgst: Number(item.sgst),
        cgst: Number(item.cgst),
      })),
      grandTotal: Number(grandTotal),
      gstEnabled: Boolean(gstEnabled),
    };
  }, [bookingDetails, invoiceData, lineItems, grandTotal, gstEnabled]);

  const handleSaveSnapshot = useCallback(async () => {
    setIsSaving(true);
    try {
      const response = await post("", {
        query: CREATE_PREFORMA_INVOICE,
        variables: buildInvoicePayload(),
      });

      const result = response?.data?.data?.create_preforma_invoice;
      if (result?.data) {
        setSavedPreforma(result.data);
        setPreformaSource("saved");
        toast.success(result.message || "Preforma invoice saved successfully!");
      } else {
        toast.error(result?.message || "Failed to save");
      }
    } catch (error) {
      console.error("Error saving preforma:", error);
      toast.error("Failed to save preforma invoice");
    } finally {
      setIsSaving(false);
    }
  }, [buildInvoicePayload, setSavedPreforma, setPreformaSource]);

  const handleUpdateSnapshot = useCallback(async () => {
    setIsSaving(true);
    try {
      const response = await post("", {
        query: UPDATE_PREFORMA_INVOICE,
        variables: buildInvoicePayload(),
      });

      const result = response?.data?.data?.update_preforma_invoice;
      if (result?.data) {
        setSavedPreforma(result.data);
        setPreformaSource("saved");
        toast.success(result.message || "Preforma invoice updated successfully!");
      } else {
        toast.error(result?.message || "Failed to update");
      }
    } catch (error) {
      console.error("Error updating preforma:", error);
      toast.error("Failed to update preforma invoice");
    } finally {
      setIsSaving(false);
    }
  }, [buildInvoicePayload, setSavedPreforma, setPreformaSource]);

  return (
    <>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="h5" fontWeight="bold">
            Proforma Invoice
          </Typography>
          {isLoadingPreforma ? (
            <CircularProgress size={20} />
          ) : (
            <Chip
              label={preformaSource === "saved" ? "Saved Snapshot" : "Calculated"}
              color={preformaSource === "saved" ? "success" : "warning"}
              size="small"
            />
          )}
        </Stack>
        <Stack direction="row" spacing={2} alignItems="center">
          {/* Editing toggle — calculated: always, saved: only when booking is pending */}
          {(preformaSource === "calculated" ||
            (savedPreforma?.status === "draft" && bookingDetails?.process_status === "pending")) && (
            <FormControlLabel
              control={
                <Switch
                  checked={isEditing}
                  onChange={onToggleEditing}
                  color="secondary"
                />
              }
              label={<Typography variant="body2">Enable Editing</Typography>}
            />
          )}

          {/* Save — only when no saved snapshot exists */}
          {preformaSource === "calculated" && (
            <Button
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Save />}
              onClick={handleSaveSnapshot}
              disabled={isSaving}
            >
              {isSaving ? "Saving..." : "Save Snapshot"}
            </Button>
          )}

          {/* Update — only for draft saved + booking pending + editing */}
          {preformaSource === "saved" &&
            savedPreforma?.status === "draft" &&
            bookingDetails?.process_status === "pending" &&
            isEditing && (
            <Button
              variant="contained"
              color="primary"
              startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : <Update />}
              onClick={handleUpdateSnapshot}
              disabled={isSaving}
            >
              {isSaving ? "Updating..." : "Update Snapshot"}
            </Button>
          )}

          <Button
            variant="outlined"
            color="info"
            startIcon={<Visibility />}
            onClick={openPreviewModal}
          >
            Preview
          </Button>
          <PDFDownloadLink
            document={
              <InvoiceDocument
                invoiceData={invoiceData}
                items={lineItems}
                bookingDetails={bookingDetails}
                hotelProfile={hotelProfile}
              />
            }
            fileName={`PI_${bookingDetails?.booking_id}.pdf`}
            style={{ textDecoration: "none" }}
          >
            {({ loading }) => (
              <Button
                variant="contained"
                color="secondary"
                startIcon={<Download />}
                disabled={loading}
              >
                {loading ? "Generating..." : "Download PDF"}
              </Button>
            )}
          </PDFDownloadLink>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 3 }} />

      {/* Info Banner */}
      {preformaSource === "saved" && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: savedPreforma?.status === "finalized" ? colors.blueAccent[700] : colors.greenAccent[700],
          }}
        >
          <Typography variant="body2" color="white">
            {savedPreforma?.status === "finalized"
              ? "📌 This is a finalized snapshot. Editing is disabled."
              : bookingDetails?.process_status === "pending"
                ? "💾 You're viewing a saved snapshot. Enable editing to make changes and click 'Update Snapshot' to save."
                : "📌 This is a saved snapshot. Booking is no longer pending, editing is disabled."}
          </Typography>
        </Paper>
      )}

      {preformaSource === "calculated" && (
        <Paper sx={{ p: 2, mb: 3, backgroundColor: colors.blueAccent[800] }}>
          <Typography variant="body2" color="white">
            ℹ️ This invoice is calculated dynamically from booking data. Click 'Save Snapshot' to persist it.
          </Typography>
        </Paper>
      )}

      {/* Content */}
      <Grid container spacing={3}>
        {/* LEFT: Header Info */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 2, backgroundColor: colors.primary[500], height: "100%" }}>
            <Typography variant="h6" mb={2} color={colors.greenAccent[500]}>
              Invoice Details
            </Typography>
            <Stack spacing={2} mb={3}>
              <TextField
                label="Invoice Number"
                name="invoiceNumber"
                value={invoiceData.invoiceNumber}
                onChange={onHeaderChange}
                disabled={!isEditing}
                size="small"
                fullWidth
                helperText={isEditing ? "You can manually override the Invoice ID" : ""}
              />
               <TextField
                label="Guest Name (From Booking)"
                name="bookingGuestName"
                value={bookingDetails.name || ""}
                disabled
                size="small"
                fullWidth
              />
            </Stack>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="h6" mb={2} color={colors.greenAccent[500]}>
              Bill To Details
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Name"
                name="guestName"
                value={invoiceData.guestName}
                onChange={onHeaderChange}
                disabled={!isEditing}
                size="small"
                fullWidth
              />
              <TextField
                label="Address"
                name="guestAddress"
                value={invoiceData.guestAddress}
                onChange={onHeaderChange}
                disabled={!isEditing}
                multiline
                rows={3}
                size="small"
                fullWidth
              />
              <TextField
                label="Mobile"
                name="guestMobile"
                value={invoiceData.guestMobile}
                onChange={onHeaderChange}
                disabled={!isEditing}
                size="small"
                fullWidth
              />
              <TextField
                label="GSTIN"
                name="guestGstin"
                value={invoiceData.guestGstin}
                onChange={onHeaderChange}
                disabled={!isEditing}
                size="small"
                fullWidth
                placeholder="Optional"
              />
            </Stack>
          </Paper>
        </Grid>

        {/* RIGHT: Line Items Table */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 0, backgroundColor: colors.primary[500], overflow: "hidden" }}>
            <LineItemsTable
              lineItems={lineItems}
              isEditing={isEditing}
              grandTotal={grandTotal}
              colors={colors}
              onLineItemChange={onLineItemChange}
              onAddItem={onAddItem}
              onRemoveItem={onRemoveItem}
              gstEnabled={gstEnabled}
            />
          </Paper>
        </Grid>
      </Grid>

      {/* Preview Modal */}
      <ProformaPreviewModal
        open={openPreview}
        onClose={closePreviewModal}
        colors={colors}
        invoiceData={invoiceData}
        lineItems={lineItems}
        bookingDetails={bookingDetails}
        hotelProfile={hotelProfile}
        gstEnabled={gstEnabled}
      />
    </>
  );
};

export default ProformaSection;